/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, forwardRef, useEffect } from "react";
import useEffectAsync from "lib/react/customHooks";
import MaterialTable from "material-table";
import {
  AddBox,
  ArrowDownward,
  Check,
  ChevronLeft,
  ChevronRight,
  Clear,
  DeleteOutline,
  Edit,
  FilterList,
  FirstPage,
  LastPage,
  Remove,
  SaveAlt,
  Search,
  ViewColumn
} from "@material-ui/icons";
import _ from "lodash";
import { getCompanies } from "lib/services/companies";
import { getAthenaTenants } from "lib/services/athenaTenants";
import DeleteCompanyComponent from "./DeleteCompanyComponent";

export default function CompaniesTable({ refreshKey }) {
  const tableIcons = {
    Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
    Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
    Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
    DetailPanel: forwardRef((props, ref) => (
      <ChevronRight {...props} ref={ref} />
    )),
    Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
    Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
    Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
    LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
    NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    PreviousPage: forwardRef((props, ref) => (
      <ChevronLeft {...props} ref={ref} />
    )),
    ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref) => (
      <ArrowDownward {...props} ref={ref} />
    )),
    ThirdStateCheck: forwardRef((props, ref) => (
      <Remove {...props} ref={ref} />
    )),
    ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />)
  };

  const [isLoading, setIsLoading] = useState(false);
  const initialRefresh = 0;
  const [refresh, setRefresh] = useState(initialRefresh);

  useEffect(() => {
    // Do not update refresh key on initial mount
    if (refreshKey !== initialRefresh) setRefresh(refresh + 1);
  }, [refreshKey]);

  const [state, setState] = useState({
    columns: [
      { title: "ID", field: "id" },
      { title: "Full name", field: "name" },
      {
        title: "Shortname",
        field: "short_name"
      }
    ],
    data: []
  });
  const [deleteDialogState, setDeleteDialogState] = useState({
    open: false
  });
  const [athenaTenants, setAthenaTenants] = useState([]);

  const closeDeleteDialog = () => {
    setDeleteDialogState({ open: false });
  };

  const handleDeleteClick = (_event, company) => {
    setDeleteDialogState({
      open: true,
      company
    });
  };

  const refreshTable = () => {
    setRefresh(refresh + 1);
  };

  const onDeleteCompanySuccess = () => {
    refreshTable();
    closeDeleteDialog();
  };

  useEffectAsync(
    async () => {
      const response = await getAthenaTenants();
      return _.map(response.data, "company_id");
    },
    setAthenaTenants,
    [refresh]
  );

  useEffectAsync(
    async () => {
      setIsLoading(true);
      const result = await getCompanies();
      return result.data;
    },
    companies => {
      setState({
        ...state,
        data: companies
      });
      setIsLoading(false);
    },
    [refresh]
  );

  return (
    <>
      <MaterialTable
        title="Companies"
        // material-table mutates the column objects it is given: on every data
        // update it rewrites column.tableData.width, wrapping the previous
        // value in another css calc(). Feeding it the same objects from state
        // makes that string grow exponentially, so hand it disposable clones
        // and keep ours pristine.
        columns={state.columns.map(column => ({ ...column }))}
        data={state.data}
        icons={tableIcons}
        isLoading={isLoading}
        options={{
          actionsColumnIndex: -1
        }}
        actions={[
          rowData => ({
            icon: DeleteOutline,
            disabled: athenaTenants.includes(rowData.id),
            tooltip: athenaTenants.includes(rowData.id)
              ? "This company has a tenant associated and can't be deleted"
              : "Delete Company",
            onClick: handleDeleteClick
          })
        ]}
      />
      <DeleteCompanyComponent
        open={deleteDialogState.open}
        company={deleteDialogState.company}
        onSuccess={onDeleteCompanySuccess}
        onDismiss={closeDeleteDialog}
      />
    </>
  );
}
